import {CacheDecisionResponseModel, DailyTransactionTypeEnum, LookupItemModel, LookupTypeEnum} from "../../models";
import {getAppConfig, getUserId, secureLocalStorageGet, secureLocalStorageRemove, secureLocalStorageSet} from "..";
import {getLookupByType} from "../../serviceBroker/lookupApiServiceBroker";
import {SystemConfiguration} from "../../configuration";
import {
    getPointOfSaleSetting,
    getUserPrivileges,
    getUserSetting,
    getUserTransactionsSettings
} from "../../serviceBroker/userApiServiceBroker";
import {getCompanySetting} from "../../serviceBroker/companySettingApiServiceBroker";
import {getAllDictionaries} from "../../serviceBroker/dictionaryApiServiceBroker";
//#region public
export const getLocalStorageCachedData = <T>(key: string): T | null => {
    const data = secureLocalStorageGet(key);
    if (!data) return null;
    try {
        return JSON.parse(data as string) as T;
    } catch (error) {
        return null;
    }
};
export const reValidateLocalStorageCachedData = async (
    type: LookupTypeEnum,userId?:number|null
) => {
    let  cacheKey: string ="";
    const currentUserId=getUserId();
    userId=userId||currentUserId;
    //const cacheDecision = shouldUseCache(type, false);
    if(userId==currentUserId ) {  //&& cacheDecision.useCache
        switch (type) {
            case LookupTypeEnum.UserSettingCacheKey:
                cacheKey = SystemConfiguration.cacheKey_Configurations.setting.currentUserSettings;
                break;
            case LookupTypeEnum.UserPermissionSettingCacheKey:
                cacheKey = SystemConfiguration.cacheKey_Configurations.miscellaneous.privileges;
                break;
            case LookupTypeEnum.UserTransactionSettingCacheKey:
                cacheKey = SystemConfiguration.cacheKey_Configurations.setting.userTransactionSettingsList;
                break;
            case LookupTypeEnum.CompanySettingCacheKey:
                cacheKey = SystemConfiguration.cacheKey_Configurations.setting.companySettings;
                break;
            case LookupTypeEnum.DictionaryCacheKey:
                cacheKey = SystemConfiguration.cacheKey_Configurations.miscellaneous.dictionary;
                break;
            case LookupTypeEnum.UserPosTransactionSettingCacheKey:
                cacheKey = SystemConfiguration.cacheKey_Configurations.setting.userPosSettings;
                break;
            default:
                cacheKey = Object.keys(LookupTypeEnum)[Object.values(LookupTypeEnum).indexOf(type)];
        }
        const data = secureLocalStorageGet(cacheKey);
        if (data === null) return;
        secureLocalStorageRemove(cacheKey);
        switch (type) {
            case LookupTypeEnum.DictionaryCacheKey:
                await fillLocalStorageCachedData(await getAllDictionaries(), cacheKey);
                break;
            case LookupTypeEnum.UserSettingCacheKey:
                await fillLocalStorageCachedData(await getUserSetting(currentUserId, false), cacheKey);
                break;
            case LookupTypeEnum.UserPermissionSettingCacheKey:
                await fillLocalStorageCachedData(await getUserPrivileges(currentUserId), cacheKey);
                break;
            case LookupTypeEnum.UserTransactionSettingCacheKey:
                await fillLocalStorageCachedData(await getUserTransactionsSettings(
                    [
                        DailyTransactionTypeEnum.SalesInvoice,
                        DailyTransactionTypeEnum.PurchasesInvoice,
                        DailyTransactionTypeEnum.SalesRefund,
                        DailyTransactionTypeEnum.PurchasesRefund,
                        DailyTransactionTypeEnum.Order,
                        DailyTransactionTypeEnum.ShowPrice,
                        DailyTransactionTypeEnum.POSSales,
                        DailyTransactionTypeEnum.POSSalesRefund,
                        DailyTransactionTypeEnum.POSRestaurant,
                        DailyTransactionTypeEnum.Reserve_Restaurant_Bill,
                        DailyTransactionTypeEnum.ReturnPOSRestaurant,
                    ],
                    false,
                    ""
                ), cacheKey);
                break;
            case LookupTypeEnum.UserPosTransactionSettingCacheKey:
                await fillLocalStorageCachedData( await getPointOfSaleSetting(getUserId(), false), cacheKey);
                break;
            case LookupTypeEnum.CompanySettingCacheKey:
                await fillLocalStorageCachedData(await getCompanySetting(), cacheKey);
                break;
            default:
                await fillLookUpLocalStorageCachedData(type);
                break;
        }
    }
};
export const getCachedDataOrFetch = async <T>(
    cacheKey: string,
    fetchFunction: () => Promise<T>
): Promise<T> => {
    const cachedData = getLocalStorageCachedData<T>(cacheKey);
    if (cachedData) {
        return cachedData;
    }
    const fetchedData = await fetchFunction();
    secureLocalStorageSet(cacheKey, JSON.stringify(fetchedData));
    return fetchedData;
};
export const shouldUseCache = (type: LookupTypeEnum):CacheDecisionResponseModel=> {//, forceCached: boolean = false
    // if (forceCached) {
    //     return { useCache: true, isFromDefault: false };
    // }
    try {
        const typeName: string = LookupTypeEnum[type];
        const cacheKey = `lookup_${typeName}`;
        if (!lookupDecisionCache) {
            lookupDecisionCache = new Map<string, CacheDecisionResponseModel>();
        }
        if (lookupDecisionCache.has(cacheKey)) {
            return lookupDecisionCache.get(cacheKey)!;
        }
        const config = getAppConfig();
        const cacheSettings = config?.cache_Configurations || {
            isDefaultCachedEnabled: false,
            dynamicLookupCacheSettings: {}
        };
        const dynamicCacheSettings = cacheSettings.dynamicLookupCacheSettings || {};
        let decision: CacheDecisionResponseModel;
        if (typeName in dynamicCacheSettings) {
            decision = {
                useCache: dynamicCacheSettings[typeName],
                isFromDefault: false,
            };
        } else {
            decision = {
                useCache: cacheSettings.isDefaultCachedEnabled,
                isFromDefault: true,
            };
        }
        // Cache the decision for future calls
        lookupDecisionCache.set(cacheKey, decision);
        return decision;
    } catch (error) {
        console.error(`Error in shouldUseCache for type ${type}:`, error);
        return {useCache: false, isFromDefault: true};
    }
};
//#endregion
//#region private
// Pre-compute a mapping of enum values to their string names (done once at module load)
const lookupTypeNames = Object.fromEntries(
    Object.entries(LookupTypeEnum).map(([key, value]) => [value, key])
) as Record<LookupTypeEnum, string>;

// Pre-normalize the configuration keys to lowercase and convert values to booleans
// This only needs to be recomputed if configuration changes
const normalizedCacheConfig: Record<string, boolean> = Object.fromEntries(
    Object.entries(SystemConfiguration.cache_Configurations).map(([key, value]) => [
        key.toLowerCase(),
        !!value,
    ])
);

// Cache for lookup decisions to avoid repeated calculations
let  lookupDecisionCache: Map<string, CacheDecisionResponseModel> = new Map();
const getLookupLocalStorageCachedData = async (
    type: LookupTypeEnum
): Promise<LookupItemModel[]> => {
    let response: LookupItemModel[];
    const cacheKey: string =
        Object.keys(LookupTypeEnum)[Object.values(LookupTypeEnum).indexOf(type)];
    const data = secureLocalStorageGet(cacheKey);
    if (data !== null && data !== undefined) {
        response = JSON.parse(data as string) as LookupItemModel[];
    } else {
        response = await getLookupByType(type, false, false);
        if (response != null && response.length != 0) {
            secureLocalStorageSet(cacheKey, JSON.stringify(response));
        }
    }
    return response;
};
const fillLookUpLocalStorageCachedData = async (
    type: LookupTypeEnum
) => {
    const cacheKey: string =
        Object.keys(LookupTypeEnum)[Object.values(LookupTypeEnum).indexOf(type)];
    const  response = await getLookupByType(type, false, false);
    if (response != null && response.length != 0) {
        secureLocalStorageSet(cacheKey, JSON.stringify(response));
    }
};
const fillLocalStorageCachedData = async (data:any,cacheKey:string)=>{
    if(data===null||data===undefined)return;
    secureLocalStorageSet(cacheKey,JSON.stringify(data));
}
//#endregion